import { scheduleSaveUserStats } from './firestoreSync';

const KEY_PREFIX = 'dumplingo_user_stats';

const getUserKey = (user) => {
  const id = user?.uid || user?.email || 'guest';
  return `${KEY_PREFIX}:${id}`;
};

const getLearnedCharactersKey = (user) => {
  const id = user?.uid || user?.id || user?.email || 'guest';
  return `learnedCharactersByStory:${id}`;
};

export const extractChineseCharacters = (story) => {
  const characters = new Set();
  const sentences = story?.sentences || story?.text || [];

  sentences.forEach((sentence) => {
    const sentenceText = sentence?.chinese || sentence?.characters || sentence?.chineseNeutral || '';
    const wordText = Array.isArray(sentence?.words)
      ? sentence.words.map(word => word?.char || word?.word || word?.chinese || '').join('')
      : '';

    for (const character of `${sentenceText}${wordText}`) {
      if (/\p{Script=Han}/u.test(character)) characters.add(character);
    }
  });

  return [...characters];
};

export const readLearnedCharactersByStory = (user) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(getLearnedCharactersKey(user)) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const readLearnedCharacters = (user, includeStory = () => true) => {
  const byStory = readLearnedCharactersByStory(user);
  return new Set(
    Object.entries(byStory)
      .filter(([storyId]) => includeStory(storyId))
      .flatMap(([, characters]) => Array.isArray(characters) ? characters : [])
  );
};

export const setStoryLearnedCharacters = (user, storyId, story, isRead) => {
  if (!storyId) return;
  const byStory = readLearnedCharactersByStory(user);

  if (isRead) byStory[storyId] = extractChineseCharacters(story);
  else delete byStory[storyId];

  try {
    localStorage.setItem(getLearnedCharactersKey(user), JSON.stringify(byStory));
    window.dispatchEvent(new CustomEvent('learned-characters-updated'));
    scheduleSaveUserStats(user?.uid);
  } catch {}
};

const dateKey = (d = new Date()) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export const readStats = (user) => {
  try {
    const raw = localStorage.getItem(getUserKey(user));
    if (raw) {
      const parsed = JSON.parse(raw);
      // ensure history shape exists
      return {
        wordsRead: parsed.wordsRead || 0,
        listeningSeconds: parsed.listeningSeconds || 0,
        listeningSecondsByStory: parsed.listeningSecondsByStory && typeof parsed.listeningSecondsByStory === 'object'
          ? parsed.listeningSecondsByStory
          : {},
        history: {
          byDay: parsed.history?.byDay || {}
        }
      };
    }
  } catch {}
  return { wordsRead: 0, listeningSeconds: 0, listeningSecondsByStory: {}, history: { byDay: {} } };
};

const writeStats = (user, stats) => {
  try {
    localStorage.setItem(getUserKey(user), JSON.stringify(stats));
    window.dispatchEvent(
      new CustomEvent('user-stats-updated', {
        detail: { userKey: getUserKey(user), stats }
      })
    );
    // Persist to Firestore so stats survive incognito / new devices
    scheduleSaveUserStats(user?.uid);
  } catch {}
};

export const addWordsRead = (user, count, when = new Date()) => {
  if (!count || count < 0) return;
  const s = readStats(user);
  s.wordsRead = Math.max(0, (s.wordsRead || 0) + Math.floor(count));
  const dk = dateKey(when);
  s.history = s.history || { byDay: {} };
  s.history.byDay = s.history.byDay || {};
  const day = s.history.byDay[dk] || { words: 0, seconds: 0 };
  day.words += Math.floor(count);
  s.history.byDay[dk] = day;
  writeStats(user, s);
};

export const addListeningSeconds = (user, seconds, storyIdOrWhen = null, when = new Date()) => {
  if (!seconds || seconds < 0) return;
  const storyId = storyIdOrWhen instanceof Date ? null : storyIdOrWhen;
  const recordedAt = storyIdOrWhen instanceof Date ? storyIdOrWhen : when;
  const s = readStats(user);
  const wholeSeconds = Math.floor(seconds);
  s.listeningSeconds = Math.max(0, (s.listeningSeconds || 0) + wholeSeconds);
  if (storyId) {
    s.listeningSecondsByStory = s.listeningSecondsByStory || {};
    s.listeningSecondsByStory[storyId] = Math.max(
      0,
      (s.listeningSecondsByStory[storyId] || 0) + wholeSeconds
    );
  }
  const dk = dateKey(recordedAt);
  s.history = s.history || { byDay: {} };
  s.history.byDay = s.history.byDay || {};
  const day = s.history.byDay[dk] || { words: 0, seconds: 0 };
  day.seconds += wholeSeconds;
  s.history.byDay[dk] = day;
  writeStats(user, s);
};

export const subscribeToUserStats = (user, cb) => {
  const key = getUserKey(user);
  const handler = (e) => {
    if (e?.detail?.userKey === key) cb(e.detail.stats);
  };
  window.addEventListener('user-stats-updated', handler);
  return () => window.removeEventListener('user-stats-updated', handler);
};

// Returns current consecutive-day reading streak (counts today if active)
export const calculateStreak = (user) => {
  const { history } = readStats(user);
  const byDay = history?.byDay || {};
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = dateKey(d);
    const day = byDay[key];
    if (day && (day.words > 0 || day.seconds > 0)) {
      streak++;
    } else if (i === 0) {
      // today has no activity yet — don't break streak, check yesterday next
      continue;
    } else {
      break;
    }
  }
  return streak;
};